import { useState, useEffect, useRef, useCallback } from 'react';
import { Rectangle, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

const boundsToArray = (start, end) => {
  const bounds = L.latLngBounds(start, end);
  return [
    [bounds.getSouth(), bounds.getWest()],
    [bounds.getNorth(), bounds.getEast()],
  ];
};

const defaultRectangleOptions = {
  color: '#2563eb',
  weight: 1.5,
  dashArray: '6',
  fillOpacity: 0.1,
};

const SelectionDrawer = ({
  selectionBounds,
  onSelectionChange,
  holdDuration = 500,
  minLatLngDelta = 0.0005,
  rectangleOptions = {},
  /** When true, touch starts drawing immediately (no long-press). Use after user explicitly enabled selection on touch devices. */
  bypassTouchHold = false,
  /**
   * When true, do not re-enable map drag/zoom after a draw ends (parent keeps gestures off until user leaves draw mode).
   */
  suppressMapGestureRestore = false,
}) => {
  const [draftBounds, setDraftBounds] = useState(null);
  const startRef = useRef(null);
  const startClientRef = useRef(null);
  const holdTimeoutRef = useRef(null);
  const isDrawingRef = useRef(false);
  const isTouchRef = useRef(false);
  const touchMovedRef = useRef(false);
  const interactionsDisabledRef = useRef(false);
  const onSelectionChangeRef = useRef(onSelectionChange);
  const minLatLngDeltaRef = useRef(minLatLngDelta);

  onSelectionChangeRef.current = onSelectionChange;
  minLatLngDeltaRef.current = minLatLngDelta;

  const map = useMapEvents({
    mousedown: (event) => handlePointerDown(event, { immediate: false }),
    touchstart: (event) => handlePointerDown(event, { immediate: false, isTouch: true }),
    mousemove: (event) => handlePointerMove(event),
    touchmove: (event) => handlePointerMove(event),
    mouseup: (event) => handlePointerUp(event),
    touchend: (event) => handlePointerUp(event),
    mouseout: handlePointerCancel,
    touchcancel: handlePointerCancel,
  });

  /**
   * Leaflet only wires certain DOM types into map.on('touchstart'); on many mobiles those listeners never run.
   * When bypassTouchHold is on, listen on the map container with capture + non-passive pointer/touch events.
   */
  useEffect(() => {
    if (!bypassTouchHold || !map) return undefined;

    const el = map.getContainer();
    let activePointerId = null;
    let touchActive = false;

    const latLngFromXY = (clientX, clientY) => {
      try {
        const pt = map.mouseEventToContainerPoint({ clientX, clientY });
        return map.containerPointToLatLng(pt);
      } catch {
        return null;
      }
    };

    const cancelHoldLocal = () => {
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
        holdTimeoutRef.current = null;
      }
    };

    const enableIfNeeded = () => {
      if (suppressMapGestureRestore) {
        interactionsDisabledRef.current = false;
        return;
      }
      if (!interactionsDisabledRef.current) return;
      interactionsDisabledRef.current = false;
      try {
        map.dragging.enable();
      } catch (e) { /* ignore */ }
      try {
        map.doubleClickZoom.enable();
      } catch (e) { /* ignore */ }
      try {
        map.touchZoom?.enable?.();
      } catch (e) { /* ignore */ }
    };

    const resetDrawingLocal = () => {
      isDrawingRef.current = false;
      startRef.current = null;
      startClientRef.current = null;
      isTouchRef.current = false;
      touchMovedRef.current = false;
      setDraftBounds(null);
      enableIfNeeded();
    };

    const startDraw = (latlng) => {
      if (!latlng) return;
      if (!interactionsDisabledRef.current) {
        interactionsDisabledRef.current = true;
        try {
          map.dragging.disable();
        } catch (e) { /* ignore */ }
        try {
          map.doubleClickZoom.disable();
        } catch (e) { /* ignore */ }
        try {
          map.touchZoom?.disable?.();
        } catch (e) { /* ignore */ }
      }
      isDrawingRef.current = true;
      const { lat, lng } = latlng;
      setDraftBounds([
        [lat, lng],
        [lat, lng],
      ]);
    };

    const finishDraw = (latlng) => {
      cancelHoldLocal();
      if (!isDrawingRef.current || !startRef.current) {
        resetDrawingLocal();
        return;
      }
      if (!latlng) {
        resetDrawingLocal();
        return;
      }

      const finalBounds = boundsToArray(startRef.current, latlng);
      const [southWest, northEast] = finalBounds;
      const latDiff = Math.abs(southWest[0] - northEast[0]);
      const lngDiff = Math.abs(southWest[1] - northEast[1]);
      const minD = minLatLngDeltaRef.current;
      const cb = onSelectionChangeRef.current;

      resetDrawingLocal();

      if (!cb) return;
      if (latDiff < minD || lngDiff < minD) {
        cb(null);
        return;
      }
      cb(finalBounds);
    };

    const onPointerDown = (ev) => {
      if (ev.pointerType === 'mouse') return;
      if (ev.isPrimary === false) return;
      try {
        if (ev.cancelable) ev.preventDefault();
      } catch (e) { /* ignore */ }

      activePointerId = ev.pointerId;
      cancelHoldLocal();
      const latlng = latLngFromXY(ev.clientX, ev.clientY);
      startRef.current = latlng;
      startClientRef.current = { x: ev.clientX, y: ev.clientY };
      isTouchRef.current = true;
      touchMovedRef.current = false;
      if (latlng) {
        startDraw(latlng);
        try {
          el.setPointerCapture(ev.pointerId);
        } catch (e) { /* ignore */ }
      }
    };

    const onPointerMove = (ev) => {
      if (activePointerId === null || ev.pointerId !== activePointerId) return;
      if (ev.pointerType === 'mouse') return;
      try {
        if (ev.cancelable) ev.preventDefault();
      } catch (e) { /* ignore */ }

      const latlng = latLngFromXY(ev.clientX, ev.clientY);
      if (!latlng || !isDrawingRef.current || !startRef.current) return;
      setDraftBounds(boundsToArray(startRef.current, latlng));
    };

    const onPointerUp = (ev) => {
      if (activePointerId === null || ev.pointerId !== activePointerId) return;
      if (ev.pointerType === 'mouse') return;
      try {
        if (ev.cancelable) ev.preventDefault();
      } catch (e) { /* ignore */ }
      try {
        el.releasePointerCapture(ev.pointerId);
      } catch (e) { /* ignore */ }

      const latlng = latLngFromXY(ev.clientX, ev.clientY);
      activePointerId = null;
      finishDraw(latlng);
    };

    const onTouchStart = (ev) => {
      if (!ev.touches || ev.touches.length !== 1) return;
      touchActive = true;
      try {
        if (ev.cancelable) ev.preventDefault();
      } catch (e) { /* ignore */ }

      const t = ev.touches[0];
      cancelHoldLocal();
      const latlng = latLngFromXY(t.clientX, t.clientY);
      startRef.current = latlng;
      startClientRef.current = { x: t.clientX, y: t.clientY };
      isTouchRef.current = true;
      touchMovedRef.current = false;
      if (latlng) startDraw(latlng);
    };

    const onTouchMove = (ev) => {
      if (!touchActive || !ev.touches || ev.touches.length !== 1) return;
      try {
        if (ev.cancelable) ev.preventDefault();
      } catch (e) { /* ignore */ }

      const t = ev.touches[0];
      const latlng = latLngFromXY(t.clientX, t.clientY);
      if (!latlng || !isDrawingRef.current || !startRef.current) return;
      setDraftBounds(boundsToArray(startRef.current, latlng));
    };

    const onTouchEnd = (ev) => {
      if (!touchActive) return;
      touchActive = false;
      const t = ev.changedTouches?.[0];
      try {
        if (ev.cancelable) ev.preventDefault();
      } catch (e) { /* ignore */ }

      const latlng = t ? latLngFromXY(t.clientX, t.clientY) : null;
      finishDraw(latlng);
    };

    const cap = { capture: true, passive: false };

    if (typeof window !== 'undefined' && window.PointerEvent) {
      el.addEventListener('pointerdown', onPointerDown, cap);
      el.addEventListener('pointermove', onPointerMove, cap);
      el.addEventListener('pointerup', onPointerUp, cap);
      el.addEventListener('pointercancel', onPointerUp, cap);
    } else {
      el.addEventListener('touchstart', onTouchStart, cap);
      el.addEventListener('touchmove', onTouchMove, cap);
      el.addEventListener('touchend', onTouchEnd, cap);
      el.addEventListener('touchcancel', onTouchEnd, cap);
    }

    return () => {
      if (typeof window !== 'undefined' && window.PointerEvent) {
        el.removeEventListener('pointerdown', onPointerDown, cap);
        el.removeEventListener('pointermove', onPointerMove, cap);
        el.removeEventListener('pointerup', onPointerUp, cap);
        el.removeEventListener('pointercancel', onPointerUp, cap);
      } else {
        el.removeEventListener('touchstart', onTouchStart, cap);
        el.removeEventListener('touchmove', onTouchMove, cap);
        el.removeEventListener('touchend', onTouchEnd, cap);
        el.removeEventListener('touchcancel', onTouchEnd, cap);
      }
      activePointerId = null;
      touchActive = false;
      if (isDrawingRef.current) {
        resetDrawingLocal();
      }
    };
  }, [bypassTouchHold, map, suppressMapGestureRestore]);

  function eventToLatLng(event) {
    if (!event) return null;
    if (event.latlng) return event.latlng;

    const oe = event.originalEvent;
    if (!oe) return null;

    // TouchEvent: use first touch point
    const touch = oe.touches?.[0] || oe.changedTouches?.[0];
    if (touch && typeof touch.clientX === 'number' && typeof touch.clientY === 'number') {
      try {
        const point = map.mouseEventToContainerPoint(touch);
        return map.containerPointToLatLng(point);
      } catch (e) {
        return null;
      }
    }

    // MouseEvent / PointerEvent fallback
    if (typeof oe.clientX === 'number' && typeof oe.clientY === 'number') {
      try {
        return map.mouseEventToLatLng(oe);
      } catch (e) {
        return null;
      }
    }

    return null;
  }

  function eventToClientXY(event) {
    const oe = event?.originalEvent;
    if (!oe) return null;
    const touch = oe.touches?.[0] || oe.changedTouches?.[0];
    const x = touch?.clientX ?? oe.clientX;
    const y = touch?.clientY ?? oe.clientY;
    if (typeof x === 'number' && typeof y === 'number') return { x, y };
    return null;
  }

  function disableInteractions() {
    if (interactionsDisabledRef.current) return;
    interactionsDisabledRef.current = true;
    try {
      map.dragging.disable();
    } catch (e) { /* ignore */ }
    try {
      map.doubleClickZoom.disable();
    } catch (e) { /* ignore */ }
    try {
      map.touchZoom?.disable?.();
    } catch (e) { /* ignore */ }
  }

  function startDrawingFromLatLng(latlng) {
    if (!latlng) return;
    isDrawingRef.current = true;
    disableInteractions();
    const { lat, lng } = latlng;
    setDraftBounds([
      [lat, lng],
      [lat, lng],
    ]);
  }

  function handlePointerDown(event, { immediate, isTouch = false }) {
    cancelHold();
    // Touch drawing is handled by DOM pointer/touch listeners (Leaflet rarely fires map.on('touch*') on mobile).
    if (bypassTouchHold && isTouch) {
      return;
    }

    const latlng = eventToLatLng(event);
    startRef.current = latlng;
    startClientRef.current = eventToClientXY(event);
    isTouchRef.current = isTouch;
    touchMovedRef.current = false;

    // On touch: use a short hold to avoid fighting map panning/scrolling.
    const delay = isTouch ? Math.min(300, holdDuration) : (immediate ? 0 : holdDuration);
    holdTimeoutRef.current = setTimeout(() => {
      if (!startRef.current) return;
      // If the user already started panning on touch, don't begin selection.
      if (isTouchRef.current && touchMovedRef.current) return;
      startDrawingFromLatLng(startRef.current);
    }, delay);
  }

  function handlePointerMove(event) {
    if (bypassTouchHold && isTouchRef.current) {
      return;
    }

    const latlng = eventToLatLng(event);
    if (!latlng) return;

    // Touch: if the user moves finger before the hold completes, treat it as pan (cancel selection).
    if (isTouchRef.current && !isDrawingRef.current && startClientRef.current) {
      const cur = eventToClientXY(event);
      if (cur) {
        const dx = Math.abs(cur.x - startClientRef.current.x);
        const dy = Math.abs(cur.y - startClientRef.current.y);
        if (dx > 10 || dy > 10) {
          touchMovedRef.current = true;
          cancelHold();
        }
      }
    }

    if (!isDrawingRef.current || !startRef.current) {
      return;
    }

    // While drawing, prevent map from panning/scrolling on touch devices.
    try {
      const oe = event?.originalEvent;
      if (oe?.cancelable) oe.preventDefault();
    } catch (e) { /* ignore */ }
    setDraftBounds(boundsToArray(startRef.current, latlng));
  }

  function handlePointerUp(event) {
    if (bypassTouchHold && isTouchRef.current) {
      return;
    }

    cancelHold();

    if (!isDrawingRef.current || !startRef.current) {
      resetDrawingState();
      return;
    }

    const latlng = eventToLatLng(event);
    if (!latlng) {
      resetDrawingState();
      return;
    }

    const finalBounds = boundsToArray(startRef.current, latlng);
    const [southWest, northEast] = finalBounds;
    const latDiff = Math.abs(southWest[0] - northEast[0]);
    const lngDiff = Math.abs(southWest[1] - northEast[1]);

    resetDrawingState();

    if (!onSelectionChange) {
      return;
    }

    if (latDiff < minLatLngDelta || lngDiff < minLatLngDelta) {
      onSelectionChange(null);
      return;
    }

    onSelectionChange(finalBounds);
  }

  function handlePointerCancel() {
    if (bypassTouchHold && isTouchRef.current) {
      return;
    }
    cancelHold();
    resetDrawingState();
  }

  const cancelHold = useCallback(() => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
  }, []);

  const enableInteractionsCallback = useCallback(() => {
    if (!interactionsDisabledRef.current) return;
    interactionsDisabledRef.current = false;
    try {
      map.dragging.enable();
    } catch (e) { /* ignore */ }
    try {
      map.doubleClickZoom.enable();
    } catch (e) { /* ignore */ }
    try {
      map.touchZoom?.enable?.();
    } catch (e) { /* ignore */ }
  }, [map]);

  function resetDrawingState() {
    isDrawingRef.current = false;
    startRef.current = null;
    startClientRef.current = null;
    isTouchRef.current = false;
    touchMovedRef.current = false;
    setDraftBounds(null);
    if (suppressMapGestureRestore) {
      interactionsDisabledRef.current = false;
      return;
    }
    enableInteractionsCallback();
  }

  useEffect(() => () => {
    enableInteractionsCallback();
    cancelHold();
  }, [map, enableInteractionsCallback, cancelHold]);

  const activeBounds = draftBounds || selectionBounds;
  const mergedRectangleOptions = {
    ...defaultRectangleOptions,
    ...rectangleOptions,
  };

  return activeBounds ? (
    <Rectangle bounds={activeBounds} pathOptions={mergedRectangleOptions} />
  ) : null;
};

export default SelectionDrawer;
